import React, { useCallback, useMemo } from 'react'
import { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import {
  FacilityLocationAutosuggest,
  TFacilityLocationAutosuggestChangeHandler,
} from 'apps/fleet-planner/components/atoms/FacilityLocationAutosuggest'
import { ILoadFilters } from 'apps/fleet-planner/types'
import { IFacilityLocation } from 'apps/fleet-planner/utils/autosuggest'

export const getLocationValue = ({
  city,
  id: facilityId,
  state,
}: ILocationSuggestion): IFacilityLocation => {
  if (facilityId) {
    return {
      facility: {
        id: facilityId,
      },
    }
  }

  if (city || state) {
    return {
      location: {
        city,
        state,
      },
    }
  }

  return {}
}

export interface IFilterInputProps<T extends Partial<ILoadFilters>> {
  value: T | undefined
  label?: string
  placeholder?: string
  dataTest?: string
  id?: string
  onChange: (change: T) => void
}

interface IFilterFacilityPickupLocationProps
  extends IFilterInputProps<
    Partial<Pick<ILoadFilters, 'pickupFacilityId' | 'pickupLocality' | 'pickupSubdivisionCode'>>
  > {}

export const FilterFacilityLocationPickup = ({
  dataTest,
  label,
  onChange: onChangeProp,
  placeholder,
  value: valueProp,
}: IFilterFacilityPickupLocationProps) => {
  const { pickupFacilityId: id, pickupLocality: city, pickupSubdivisionCode: state } =
    valueProp || {}

  const changeHandler = useCallback<TFacilityLocationAutosuggestChangeHandler>(
    change => {
      const { facility, location } = change || {}
      if (location?.id) {
        const { city: pickupLocality, state: pickupSubdivisionCode } = location || {}
        onChangeProp({ pickupLocality, pickupSubdivisionCode, pickupFacilityId: undefined })
      } else if (facility?.id) {
        const { id: pickupFacilityId } = facility || {}
        onChangeProp({
          pickupFacilityId,
          pickupLocality: undefined,
          pickupSubdivisionCode: undefined,
        })
      } else {
        onChangeProp({
          pickupLocality: undefined,
          pickupSubdivisionCode: undefined,
          pickupFacilityId: undefined,
        })
      }
    },
    [onChangeProp]
  )

  const value = useMemo(() => getLocationValue({ city, state, id }), [city, id, state])

  return (
    <FacilityLocationAutosuggest
      data-test={dataTest}
      label={label}
      placeholder={placeholder}
      onChange={changeHandler}
      value={value}
    />
  )
}

interface IFilterFacilityDropoffLocationProps
  extends IFilterInputProps<
    Partial<Pick<ILoadFilters, 'dropoffFacilityId' | 'dropoffLocality' | 'dropoffSubdivisionCode'>>
  > {}

export const FilterFacilityDropoffLocation = ({
  dataTest,
  label,
  onChange: onChangeProp,
  placeholder,
  value: valueProp,
}: IFilterFacilityDropoffLocationProps) => {
  const { dropoffFacilityId: id, dropoffLocality: city, dropoffSubdivisionCode: state } =
    valueProp || {}

  const changeHandler = useCallback<TFacilityLocationAutosuggestChangeHandler>(
    change => {
      const { facility, location } = change || {}
      if (location?.id) {
        const { city: dropoffLocality, state: dropoffSubdivisionCode } = location || {}
        onChangeProp({ dropoffLocality, dropoffSubdivisionCode, dropoffFacilityId: undefined })
      } else if (facility?.id) {
        const { id: dropoffFacilityId } = facility || {}
        onChangeProp({
          dropoffFacilityId,
          dropoffLocality: undefined,
          dropoffSubdivisionCode: undefined,
        })
      } else {
        onChangeProp({
          dropoffLocality: undefined,
          dropoffSubdivisionCode: undefined,
          dropoffFacilityId: undefined,
        })
      }
    },
    [onChangeProp]
  )

  const value = useMemo(() => getLocationValue({ city, state, id }), [city, id, state])

  return (
    <FacilityLocationAutosuggest
      data-test={dataTest}
      label={label}
      placeholder={placeholder}
      onChange={changeHandler}
      value={value}
    />
  )
}
